import { useState } from "react";
import { z } from "zod";
import { Loader2, KeyRound } from "lucide-react";
import { supabase, getTenantHeaders } from "@/integrations/supabase/tenantClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/common/Logo";
import { Separator } from "@/components/ui/separator";

const identifierSchema = z.string()
  .min(1, "Email or username is required")
  .max(255, "Email or username must be less than 255 characters");

const passwordSchema = z.string()
  .min(1, "Password is required")
  .max(72, "Password must be less than 72 characters");

interface LoginFormProps {
  onSuccess: (email: string) => void;
  onForgotPassword: () => void;
  onSSOLogin: () => void;
}

export function LoginForm({ onSuccess, onForgotPassword, onSSOLogin }: LoginFormProps) {
  const { toast } = useToast();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [identifierError, setIdentifierError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const resolveUserFromIdentifier = async (id: string): Promise<{ email: string } | null> => {
    try {
      const { data, error } = await supabase.functions.invoke("resolve-user-identifier", {
        body: { identifier: id },
        headers: getTenantHeaders(),
      });

      if (error || !data?.found) {
        return null;
      }

      return { email: data.email };
    } catch {
      return null;
    }
  };

  const trackLoginAttempt = async (success: boolean, identifier: string) => {
    try {
      await supabase.functions.invoke("track-login-attempt", {
        body: { identifier, success },
        headers: getTenantHeaders(),
      });
    } catch (error) {
      console.error("Error tracking login attempt:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIdentifierError(null);
    setPasswordError(null);

    // Validate identifier
    const identifierResult = identifierSchema.safeParse(identifier);
    if (!identifierResult.success) {
      setIdentifierError(identifierResult.error.errors[0].message);
      return;
    }

    // Validate password
    const passwordResult = passwordSchema.safeParse(password);
    if (!passwordResult.success) {
      setPasswordError(passwordResult.error.errors[0].message);
      return;
    }

    setLoading(true);

    try {
      // Resolve identifier to email
      let email = identifier;
      if (!identifier.includes("@")) {
        const userData = await resolveUserFromIdentifier(identifier);
        if (!userData) {
          setIdentifierError("Invalid credentials");
          setLoading(false);
          return;
        }
        email = userData.email;
      }

      // Attempt sign in
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        await trackLoginAttempt(false, email);
        
        // Generic error message to prevent enumeration
        toast({
          title: "Sign in failed",
          description: "Invalid credentials. Please try again.",
          variant: "destructive",
        });
        setPassword("");
        setLoading(false);
        return;
      }

      await trackLoginAttempt(true, email);
      onSuccess(email);
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Logo and title - Left aligned with better spacing */}
      <div className="flex flex-col items-start space-y-6">
        <Logo size="lg" />
      <div className="text-left space-y-2">
          <h1 className="text-2xl font-realhead tracking-tight text-white">Sign in to get started</h1>
          <p className="text-gray-400">Enter your information below to log in</p>
        </div>
      </div>

      {/* Form without card - directly on gradient background */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="identifier" className="text-gray-300">Email</Label>
          <Input
            id="identifier"
            type="text"
            placeholder="user@example.com"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            disabled={loading}
            autoComplete="username"
            autoFocus
          />
          {identifierError && (
            <p className="text-sm text-destructive">{identifierError}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-gray-300">Password</Label>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            autoComplete="current-password"
          />
          {passwordError && (
            <p className="text-sm text-destructive">{passwordError}</p>
          )}
        </div>

        {/* Forgot password - directly below password field, before button */}
        <div className="flex justify-end -mt-2">
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-sm text-primary hover:underline"
          >
            Forgot Password?
          </button>
        </div>

        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Signing in...
            </>
          ) : (
            "Sign in"
          )}
        </Button>
      </form>

      {/* SSO Divider and Button */}
      <div className="flex flex-col gap-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <Separator className="w-full" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-[#0c1220] px-2 text-muted-foreground">or</span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={onSSOLogin}
        >
          <KeyRound className="mr-2 h-4 w-4" />
          Sign in with SSO
        </Button>
      </div>
    </div>
  );
}
